import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { ValorSessao } from '../../nucleo/sessao';
import type { FichaLigada } from './tipos';

type SessaoFicha = Pick<ValorSessao, 'pode' | 'instalado' | 'paginaLiberada'>;

/**
 * Val6 (29/10, ficha `FUN_editar-convite-e-abrir-ficha`): o caminho da ficha ligada a um convite ou membro, ou null quando quem
 * vê não a abre. O servidor só manda a ficha que a pessoa vê (permissão de ver e empresa, pela RLS); aqui confere de novo o
 * módulo, a permissão e a página (a que foi desmarcada no papel ou retirada da pessoa não vira link).
 */
export function caminhoDaFicha(s: SessaoFicha, ficha: FichaLigada | null | undefined): string | null {
  if (!ficha) return null;
  if (ficha.tipo === 'funcionario') {
    return s.instalado('pessoas') && s.pode('pessoas.ver') && s.paginaLiberada('pessoas', 'funcionarios') ? `/pessoas/pessoas/funcionario/${ficha.id}` : null;
  }
  return s.instalado('cadastros') && s.pode('cadastros.ver') && s.paginaLiberada('cadastros', 'pessoas') ? `/cadastros/cadastros/parceiro/${ficha.id}` : null;
}

const CLASSE_LINK = 'text-acento-forte hover:underline';

/**
 * O nome da pessoa: link para a ficha (quando quem vê a abre), botão para o detalhe (`aoAbrirDetalhe`, o convidado sem ficha) ou
 * texto. O clique não chega à linha (a linha do Membros abre a gaveta).
 */
export function NomeComFicha({
  s,
  ficha,
  children,
  rotulo,
  aoAbrirDetalhe,
  rotuloDetalhe,
  className = '',
  title,
}: {
  s: SessaoFicha;
  ficha: FichaLigada | null | undefined;
  children: ReactNode;
  /** Texto acessível do link ("Abrir a ficha de ..."). */
  rotulo: string;
  aoAbrirDetalhe?: () => void;
  rotuloDetalhe?: string;
  className?: string;
  /** Texto inteiro quando o nome é cortado (`truncate`) e não vira link. */
  title?: string;
}) {
  const caminho = caminhoDaFicha(s, ficha);
  if (caminho) {
    return (
      <Link to={caminho} data-ficha={ficha!.tipo} className={`${CLASSE_LINK} ${className}`} title={rotulo} aria-label={rotulo} onClick={(e) => e.stopPropagation()}>
        {children}
      </Link>
    );
  }
  if (aoAbrirDetalhe) {
    return (
      <button
        type="button"
        data-detalhe-convite=""
        className={`${CLASSE_LINK} text-left ${className}`}
        title={rotuloDetalhe}
        aria-label={rotuloDetalhe}
        onClick={(e) => {
          e.stopPropagation();
          aoAbrirDetalhe();
        }}
      >
        {children}
      </button>
    );
  }
  return <span className={className} title={title}>{children}</span>;
}
